import { BrowserModule } from '@angular/platform-browser';
import {
  NgModule,
  NO_ERRORS_SCHEMA,
  inject,
  provideAppInitializer,
} from '@angular/core';
import * as Sentry from '@sentry/angular';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '@env/environment';

import { PerformanceMonitoringService } from '@angular/fire/compat/performance';
import {
  AUTH_INTERCEPTOR,
  AuthEffects,
  authReducer,
  ERROR_INTERCEPTOR,
  ProfileService,
  provideOutlawConfig,
  UserEffects,
  userReducer,
} from '@outlaw/core';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { metaReducers } from '@app/util/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { RouteReuseStrategy } from '@angular/router';
import { CustomReuseStrategy } from '@app/reuse-strategy';
import {
  feedReducer,
  FeedEffects,
  LineLoaderComponent,
} from '@outlaw/community';
import { MatDialogModule } from '@angular/material/dialog';
import { LayoutModule } from '@app/layout/layout.module';
import { NgOptimizedImage } from '@angular/common';
import { ThemeService } from '@app/layout/services/theme.service';
import { provideLottieOptions } from 'ngx-lottie';
import player from 'lottie-web';
import { provideCommunity } from '@outlaw/community';
import { outlawCoreConfig } from './core/configs/outlaw-core.config';
import { outlawCommunityConfig } from './core/configs/community.config';
import { provideNgxSkeletonLoader } from 'ngx-skeleton-loader';
import { provideSentry } from './core/configs/sentry.provider';
import { provideFirebase } from './core/configs/firebase.provider';

const LottieProvider = provideLottieOptions({ player: () => player });

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  schemas: [NO_ERRORS_SCHEMA],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    MatDialogModule,
    LineLoaderComponent,
    // CarouselModule,
    !environment.production
      ? StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: true })
      : [],
    StoreModule.forRoot(
      {
        auth: authReducer,
        user: userReducer,
        feed: feedReducer,
      },
      { metaReducers },
    ),
    LayoutModule,
    NgOptimizedImage,
    EffectsModule.forRoot([AuthEffects, UserEffects, FeedEffects]),
  ],
  providers: [
    provideOutlawConfig(outlawCoreConfig),
    provideCommunity(outlawCommunityConfig),
    PerformanceMonitoringService,
    CookieService,
    ProfileService,
    AUTH_INTERCEPTOR,
    ERROR_INTERCEPTOR,
    { provide: Window, useValue: window },
    { provide: RouteReuseStrategy, useClass: CustomReuseStrategy },
    ...provideSentry(),
    ...provideFirebase(),
    provideNgxSkeletonLoader({
      animation: 'progress-dark',
      theme: {
        extendsFromRoot: true,
      },
    }),
    provideAppInitializer(() => {
      const themeService = inject(ThemeService);

      if (environment.production && environment.sentry.dsn) {
        inject(Sentry.TraceService);
      }

      return themeService.initializeTheme();
    }),

    LottieProvider,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
